import React, { createContext, useState, useEffect, useContext } from 'react';
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  confirmPasswordReset,
  updatePassword,
  signInAnonymously
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { useTheme } from './ThemeContext';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idToken, setIdToken] = useState(null);
  const { loadUserPreferences, saveUserPreferences } = useTheme();

  // Register new user with email/password
  const signup = async (email, password) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    // Clear guest flags when registering a new account
    localStorage.removeItem('isGuestUser');
    localStorage.removeItem('guestCredentials');
    return userCredential;
  };

  // Login with email/password
  const login = async (email, password) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    // Clear guest flags when logging in with a regular account
    localStorage.removeItem('isGuestUser');
    localStorage.removeItem('guestCredentials');
    return userCredential;
  };

  // Guest/Anonymous login
  // Fallback: If anonymous auth fails, create a temporary guest account
  const loginAsGuest = async () => {
    try {
      // Try Firebase anonymous auth first
      const userCredential = await signInAnonymously(auth);
      return userCredential;
    } catch (error) {
      console.error('Anonymous auth failed, using fallback guest account:', error);
      
      // Fallback: Create a temporary guest account with a unique email
      const guestId = 'guest_' + Date.now() + Math.random().toString(36).substring(7);
      const guestEmail = `${guestId}@guest.calmguard.local`;
      const guestPassword = Math.random().toString(36).substring(2) + Math.random().toString(36).substring(2);
      
      try {
        // Create temporary guest account
        const guestCredential = await createUserWithEmailAndPassword(auth, guestEmail, guestPassword);
        
        // Mark this as a guest user in local storage
        localStorage.setItem('isGuestUser', 'true');
        localStorage.setItem('guestCredentials', JSON.stringify({ email: guestEmail, password: guestPassword }));
        
        return guestCredential;
      } catch (createError) {
        console.error('Failed to create guest account:', createError);
        throw error; // Throw original error
      }
    }
  };

  // Logout
  const logout = async () => {
    // Clear notification timer so it resets on next login
    localStorage.removeItem('calmguard_last_notification_time');
    // Clear login splash state so splash shows again on next login
    sessionStorage.removeItem('hasSeenLoginSplash');
    await signOut(auth);
    setIdToken(null);
  };

  // Send password reset email (Firebase handles this automatically)
  const resetPassword = async (email) => {
    await sendPasswordResetEmail(auth, email);
  };

  // Confirm password reset with code from email
  const confirmPasswordResetWithCode = async (oobCode, newPassword) => {
    await confirmPasswordReset(auth, oobCode, newPassword);
  };

  // Change password for logged-in user
  const changePassword = async (newPassword) => {
    if (!currentUser) throw new Error('No user logged in');
    await updatePassword(currentUser, newPassword);
  };

  // Get ID token for API calls
  const getToken = async () => {
    if (currentUser) {
      const token = await currentUser.getIdToken();
      return token;
    }
    return null;
  };

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        // Get and store the ID token
        const token = await user.getIdToken();
        setIdToken(token);
        localStorage.setItem('token', token);
        
        // Load user's theme preferences from backend
        if (!user.isAnonymous) {
          loadUserPreferences(token);
        }
      } else {
        setIdToken(null);
        localStorage.removeItem('token');
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, [loadUserPreferences]);

  const value = {
    currentUser,
    idToken,
    signup,
    login,
    loginAsGuest,
    logout,
    resetPassword,
    confirmPasswordResetWithCode,
    changePassword,
    getToken,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
